import React, { useEffect, useMemo, useState } from 'react';
import styled, {css} from 'styled-components';
import theme from '../../../UI/theme';
import Button from '../../UiComponents/Button';
import { deleteCustomSqlQuery, getCustomSqlData, testCustomSqlQuery, updateCustomSqlQuery } from '../../../scripts/customSql';
import CustomSqlTableModal from './CustomSqlTableModal';
import { normalizeNotificationChannel } from './CustomSqlNotificationEditor';

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    height: 100%;
    padding: 16px;
    box-sizing: border-box;
    background: ${theme.colors.darkSpace90};
    border: 1px solid ${theme.colors.grayBorder};
    border-radius: 8px;
    color: ${theme.colors.whiteSpace};

    max-width: ${({ $maxWidth }) => $maxWidth || '720px'};
    max-height: ${({ $maxHeight }) => $maxHeight || '600px'};
    min-height: ${({ $minHeight }) => $minHeight || '600px'};
`;

const TableHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HeaderTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    h3 {
        margin: 0;
        font-size: ${theme.fontSize.larger};
        color: ${theme.colors.whiteSpace};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const HeaderDescription = styled.p`
    margin: 0;
    font-size: ${theme.fontSize.small};
    color: ${theme.colors.textGray};
`;

const TableFilterContainer = styled.div`
    min-height: 24px;
    border-bottom: 1px solid ${theme.colors.grayBorder};
`;

const TableBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    border-radius: 6px;
    background: ${theme.colors.darkSpace80};
`;

const StyledTable = styled.table`
    width: 100%;
    min-width: max-content;
    border-collapse: collapse;
    font-size: ${theme.fontSize.tiny};
`;

const TableHeadCell = styled.th`
    position: sticky;
    top: 0;
    padding: 10px 12px;
    text-align: left;
    background: ${theme.colors.darkSpace70};
    color: ${theme.colors.whiteSpace};
    border-bottom: 1px solid ${theme.colors.grayBorder};
    white-space: nowrap;
    cursor: ${({ $isSortable }) => ($isSortable ? 'pointer' : 'default')};
    user-select: ${({ $isSortable }) => ($isSortable ? 'none' : 'auto')};

    &:hover {
        background: ${({ $isSortable }) =>
            $isSortable ? theme.colors.grayHover : theme.colors.darkSpace70};
    }
`;

const TableRow = styled.tr`
    &:nth-child(even) td:not(:first-child) {
        background: ${theme.colors.darkSpace70};
    }

    &:nth-child(even) td:first-child {
        background: ${theme.colors.darkSpace60};
    }

    &:hover td {
        background: ${theme.colors.grayHover};
    }

    ${({ $isBroken }) =>
        $isBroken &&
        css`
            & td {
                color: ${theme.colors.whiteSpace};
            }

            &:nth-child(odd) td {
                background: color-mix(
                    in srgb,
                    ${theme.colors.raspberryRed} 80%,
                    transparent
                ) !important;
            }

            /* Even broken rows = raspberryRed blended with transparent (≈ 0.5 opacity) */
            &:nth-child(even) td {
                background: color-mix(
                    in srgb,
                    ${theme.colors.raspberryRed} 50%,
                    transparent
                ) !important;
            }

            &:hover td {
                background: ${theme.colors.raspberryRed} !important;
            }
        `}
`;

const TableCell = styled.td`
    padding: 8px 12px;
    border-bottom: 1px solid ${theme.colors.grayBorder};
    background: ${({ isFirstColumn, $isBroken }) =>
        $isBroken
            ? theme.colors.raspberryRed
            : isFirstColumn
                ? theme.colors.darkSpace70
                : theme.colors.darkSpace80};
    color: ${theme.colors.whiteSpace};
    white-space: nowrap;
`;

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: ${theme.colors.textGray};
`;

const BrokenConditionsContainer = styled.div`
    margin-top: 4px;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid ${theme.colors.raspberryRed};
    background: ${theme.colors.darkSpace80};
    color: ${theme.colors.whiteSpace};
    font-size: ${theme.fontSize.small};
`;

const BrokenConditionsList = styled.ul`
    margin: 8px 0 0 16px;
    padding: 0;
`;

const BrokenConditionItem = styled.li`
    margin-bottom: 4px;
`;

const formatCellValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return '[object]';
        }
    }

    return String(value);
};

const formatCsvValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    let stringValue = value;

    if (typeof value === 'object') {
        try {
            stringValue = JSON.stringify(value);
        } catch (error) {
            stringValue = '[object]';
        }
    }

    const normalized = String(stringValue);
    if (/[",\n]/.test(normalized)) {
        return `"${normalized.replace(/"/g, '""')}"`;
    }

    return normalized;
};

const getComparableValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'number') {
        return value;
    }

    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return '';
        }
    }

    const stringValue = String(value).trim();
    const numericValue = Number(stringValue);

    if (stringValue !== '' && Number.isFinite(numericValue)) {
        return numericValue;
    }

    const dateValue = Date.parse(stringValue);

    if (!Number.isNaN(dateValue)) {
        return dateValue;
    }

    return stringValue.toLowerCase();
};

const formatBrokenCondition = (condition) => {
    if (!condition || typeof condition !== 'object') {
        return 'Unknown condition';
    }

    const { kind, operator, value, column } = condition;

    if (kind === 'rowCount') {
        return `Row count ${operator || ''} ${value}`.trim();
    }

    if (kind === 'columnValue') {
        return `${column || 'column'} ${operator || '='} ${value}`;
    }

    if (kind === 'columnContains') {
        return `${column || 'column'} contains "${value}"`;
    }

    if (kind === 'timeSince') {
        return `${column || 'column'} more than ${value} minute${value === 1 ? '' : 's'} ago`;
    }

    return `${kind || 'condition'} ${operator || ''} ${value ?? ''}`.trim();
};

const DATA_STATES = {
    FETCHING: 'FETCHING',
    FETCHED: 'FETCHED',
    ERROR: 'ERROR',
}

const CustomSqlTable = ({
    query = {},
    maxWidth,
    maxHeight,
    showEditButton = true,
    databaseStructure = false,
    refreshKey = 0,
}) => {
    const tableId = query?.queryId ?? 'unknown';
    const shouldFetchData = Boolean(tableId && tableId !== 'unknown');
    const [table, setTable] = useState(() => ({
        ...query,
        data: Array.isArray(query?.data) ? query.data : [],
    }));
    const [dataState, setDataState] = useState(
        shouldFetchData ? DATA_STATES.FETCHING : DATA_STATES.FETCHED
    );
    const [fetchError, setFetchError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [displayName, setDisplayName] = useState(table?.name || 'Table Name');
    const [displayDescription, setDisplayDescription] = useState(table?.description || 'Description missing');
    const [displayChatGptPrompt, setDisplayChatGptPrompt] = useState(table?.cgptPrompt || '');
    const initialQuery = useMemo(() => {
        const rawQuery = table?.query || table?.sqlQuery || table?.sql || '';
        return typeof rawQuery === 'string' ? rawQuery.trim() : '';
    }, [table?.query, table?.sql, table?.sqlQuery]);

    useEffect(() => {
        setTable((previous) => ({
            ...previous,
            ...query,
            data: Array.isArray(query?.data) ? query.data : previous?.data ?? [],
        }));
    }, [query]);

    useEffect(() => {
        if (!shouldFetchData) {
            setDataState(DATA_STATES.FETCHED);
            setFetchError(null);
            return;
        }

        let isActive = true;

        const fetchData = async () => {
            setDataState(DATA_STATES.FETCHING);
            setFetchError(null);
            try {
                const data = await getCustomSqlData(tableId);
                if (!isActive) return;

                if (data?.table) {
                    setTable((previous) => ({
                        ...previous,
                        ...data.table,
                    }));
                    setDataState(DATA_STATES.FETCHED);
                } else {
                    setTable((previous) => ({
                        ...previous,
                        data: [],
                    }));
                    setDataState(DATA_STATES.ERROR);
                    setFetchError(new Error('No table data returned'));
                }
            } catch (error) {
                if (!isActive) return;
                setDataState(DATA_STATES.ERROR);
                setFetchError(error);
            }
        };

        fetchData();

        return () => {
            isActive = false;
        };
    }, [shouldFetchData, tableId, refreshKey]);

    const [displayQuery, setDisplayQuery] = useState(initialQuery);
    const initialColumnOrder = useMemo(() => {
        return Array.isArray(table?.columnOrder)
            ? table.columnOrder.filter(Boolean)
            : [];
    }, [table?.columnOrder]);

    const [displayColumnOrder, setDisplayColumnOrder] = useState(initialColumnOrder);
    const initialTableNumber = useMemo(() => {
        if (table?.tableNumber === null || table?.tableNumber === undefined) {
            return '';
        }
        return String(table.tableNumber).trim();
    }, [table?.tableNumber]);
    const [displayTableNumber, setDisplayTableNumber] = useState(initialTableNumber);
    const initialTableGroup = useMemo(() => {
        const group = table?.tableGroup;
        if (typeof group !== 'string') {
            return '';
        }
        return group.trim();
    }, [table?.tableGroup]);
    const [displayTableGroup, setDisplayTableGroup] = useState(initialTableGroup);
    const initialNotifyIf = useMemo(() => {
        if (!Array.isArray(table?.notifyIf)) {
            return [];
        }

        return table.notifyIf
            .map((group) => (Array.isArray(group) ? group.filter(Boolean) : []))
            .filter((group) => group.length > 0);
    }, [table?.notifyIf]);
    const [displayNotifyIf, setDisplayNotifyIf] = useState(initialNotifyIf);
    const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

    const rows = useMemo(() => {
        if (!Array.isArray(table.data)) {
            return [];
        }

        return table.data;
    }, [table.data]);

    const emptyStateMessage = useMemo(() => {
        if (dataState === DATA_STATES.FETCHING) {
            return 'Loading data...';
        }

        if (dataState === DATA_STATES.ERROR) {
            return fetchError?.message
                ? `Unable to load data: ${fetchError.message}`
                : 'Unable to load data.';
        }

        return 'No data available.';
    }, [dataState, fetchError]);

    const brokenTableConditions = useMemo(() => {
        if (!Array.isArray(table?.brokenTableConditions?.table)) {
            return [];
        }

        return table?.brokenTableConditions?.table.filter(Boolean);
    }, [table?.brokenTableConditions?.table]);

    const brokenRowConditions = useMemo(() => {
        if (!Array.isArray(table?.brokenTableConditions?.row)) {
            return [];
        }

        return table?.brokenTableConditions?.row.filter(Boolean);
    }, [table?.brokenTableConditions?.row]);

    const columns = useMemo(() => {
        if (rows.length === 0) {
            return [];
        }

        const availableKeys = Object.keys(rows[0]).filter((key) => key !== '_meta');
        const orderedKeys = [];

        displayColumnOrder.forEach((key) => {
            if (availableKeys.includes(key) && !orderedKeys.includes(key)) {
                orderedKeys.push(key);
            }
        });

        availableKeys.forEach((key) => {
            if (!orderedKeys.includes(key)) {
                orderedKeys.push(key);
            }
        });

        return orderedKeys;
    }, [rows, displayColumnOrder]);

    const sortedRows = useMemo(() => {
        if (!sortConfig?.column || rows.length === 0) {
            return rows;
        }

        const { column, direction } = sortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;

        return [...rows].sort((rowA, rowB) => {
            const aValue = getComparableValue(rowA[column]);
            const bValue = getComparableValue(rowB[column]);

            if (aValue < bValue) {
                return -1 * multiplier;
            }

            if (aValue > bValue) {
                return 1 * multiplier;
            }

            return 0;
        });
    }, [rows, sortConfig]);

    useEffect(() => {
        const name = table?.name || 'Table Name';
        const description = table?.description || 'Description missing';

        setDisplayName(name);
        setDisplayDescription(description);
        setDisplayQuery(initialQuery);
        setDisplayColumnOrder(initialColumnOrder);
        setDisplayTableNumber(initialTableNumber);
        setDisplayTableGroup(initialTableGroup);
        setDisplayNotifyIf(initialNotifyIf);
        setSortConfig({ column: null, direction: 'asc' });
    }, [
        table?.name,
        table?.description,
        initialQuery,
        initialColumnOrder,
        initialTableNumber,
        initialTableGroup,
        initialNotifyIf,
        tableId,
    ]);

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleModalSave = async ({
        name,
        description,
        cgptPrompt,
        sqlQuery,
        columnOrder,
        tableNumber,
        tableGroup,
        notifyIf,
    }) => {
        const sanitizedName = name || 'Table Name';
        const sanitizedDescription = description || 'Description missing';
        const sanitizedChatGptPrompt = cgptPrompt || '';
        const sanitizedSql = sqlQuery || '';
        const sanitizedOrder = Array.isArray(columnOrder) ? columnOrder : [];
        const sanitizedTableNumber =
            tableNumber !== undefined && tableNumber !== null ? String(tableNumber) : '';
        const sanitizedTableGroup = typeof tableGroup === 'string' ? tableGroup.trim() : '';
        const sanitizedNotifyIf = Array.isArray(notifyIf)
            ? notifyIf
                  .map((group) => {
                      if (!Array.isArray(group)) {
                          return [];
                      }

                      return group
                          .filter((condition) => condition && typeof condition === 'object')
                          .map((condition) => {
                              const { kind, operator, value, column } = condition;
                              const slackNotification =
                                  condition?.slackNotification === true ||
                                  condition?.slackNotification === 1;
                              const notificationChannel = normalizeNotificationChannel(
                                  condition?.notificationChannel
                              );
                              const createHubspotTicket = condition?.createHubspotTicket === true;
                              const heading = createHubspotTicket && typeof condition?.ticket?.heading === 'string'
                                  ? condition.ticket.heading.trim()
                                  : '';
                              const pipeline = createHubspotTicket && condition?.ticket?.pipeline?.idValue
                                  ? { label: condition.ticket.pipeline.label || '', idValue: condition.ticket.pipeline.idValue }
                                  : null;
                              const stage = createHubspotTicket && condition?.ticket?.stage?.idValue
                                  ? { label: condition.ticket.stage.label || '', idValue: condition.ticket.stage.idValue }
                                  : null;
                              const ticketLabel = createHubspotTicket && condition?.ticket?.label?.idValue
                                  ? { label: condition.ticket.label.label || '', idValue: condition.ticket.label.idValue }
                                  : null;

                              if (!kind || value === undefined || value === null || value === '') {
                                  return null;
                              }

                              if (kind === 'rowCount') {
                                  const numericValue = Number(value);
                                  if (!Number.isFinite(numericValue)) {
                                      return null;
                                  }
                                  return {
                                      kind,
                                      operator,
                                      value: numericValue,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              if (kind === 'columnValue') {
                                  const normalizedColumn = typeof column === 'string' ? column.trim() : '';
                                  if (!normalizedColumn || !operator) {
                                      return null;
                                  }
                                  return {
                                      kind,
                                      operator,
                                      column: normalizedColumn,
                                      value,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              if (kind === 'columnContains') {
                                  const normalizedColumn = typeof column === 'string' ? column.trim() : '';
                                  const stringValue = value === undefined || value === null ? '' : String(value);
                                  if (!normalizedColumn || stringValue.trim() === '') {
                                      return null;
                                  }
                                  return {
                                      kind: 'columnContains',
                                      column: normalizedColumn,
                                      value: stringValue,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              if (kind === 'timeSince') {
                                  const numericValue = Number(value);
                                  const normalizedColumn = typeof column === 'string' ? column.trim() : '';
                                  if (!normalizedColumn || !Number.isFinite(numericValue)) {
                                      return null;
                                  }
                                  return {
                                      kind,
                                      column: normalizedColumn,
                                      value: numericValue,
                                      slackNotification,
                                      notificationChannel,
                                      createHubspotTicket,
                                      ticket: { heading, pipeline, stage, label: ticketLabel },
                                  };
                              }

                              return null;
                          })
                          .filter(Boolean);
                  })
                  .filter((group) => group.length > 0)
            : [];

        setDisplayName(sanitizedName);
        setDisplayDescription(sanitizedDescription);
        setDisplayChatGptPrompt(sanitizedChatGptPrompt);
        setDisplayQuery(sanitizedSql);
        setDisplayColumnOrder(sanitizedOrder);
        setDisplayTableNumber(sanitizedTableNumber);
        setDisplayTableGroup(sanitizedTableGroup);
        setDisplayNotifyIf(sanitizedNotifyIf);

        await updateCustomSqlQuery({
            id: tableId,
            name: sanitizedName,
            description: sanitizedDescription,
            cgptPrompt: sanitizedChatGptPrompt,
            sqlQuery: sanitizedSql,
            columnOrder: sanitizedOrder,
            tableNumber: sanitizedTableNumber,
            tableGroup: sanitizedTableGroup,
            notifyIf: sanitizedNotifyIf,
        });

        setShowModal(false);
    };

    const handleDelete = async () => {
        await deleteCustomSqlQuery(tableId);
        setShowModal(false);
    };

    const handleColumnHeaderClick = (column) => {
        setSortConfig((previous) => {
            if (previous?.column === column) {
                const nextDirection = previous.direction === 'asc' ? 'desc' : 'asc';
                return { column, direction: nextDirection };
            }

            return { column, direction: 'asc' };
        });
    };

    const handleColumnHeaderKeyDown = (event, column) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            handleColumnHeaderClick(column);
        }
    };

    const handleDownloadCsv = () => {
        if (columns.length === 0 || sortedRows.length === 0) {
            return;
        }

        const csvLines = [];
        csvLines.push(columns.map((column) => formatCsvValue(column)).join(','));

        sortedRows.forEach((row) => {
            const line = columns.map((column) => formatCsvValue(row[column])).join(',');
            csvLines.push(line);
        });

        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const sanitizedName = (displayName || 'custom-table')
            .trim()
            .replace(/[^a-z0-9-_]+/gi, '_')
            .replace(/_{2,}/g, '_')
            .replace(/^_+|_+$/g, '') || 'custom-table';
        link.href = URL.createObjectURL(blob);
        link.download = `${sanitizedName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    return (
    <TableContainer
        data-testid={`custom-sql-table-${tableId}`}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
    >
            <TableHeader>
                <HeaderTop>
                    <h3 title={displayName}>{displayName}</h3>
                    <HeaderActions>
                        <Button
                            tertiary
                            type="button"
                            disabled={columns.length === 0 || sortedRows.length === 0}
                            onClick={handleDownloadCsv}
                        >
                            Download CSV
                        </Button>
                        {showEditButton && (
                            <Button
                                primary
                                type="button"
                                onClick={handleOpenModal}
                            >
                                Edit
                            </Button>
                        )}
                    </HeaderActions>
                </HeaderTop>
                <HeaderDescription title={displayDescription}>
                    {displayDescription}
                </HeaderDescription>
                {(brokenTableConditions.length > 0 || brokenRowConditions.length > 0) && (
                    <BrokenConditionsContainer>

                        <strong>Broken conditions</strong>
                        {brokenTableConditions.length > 0 && (<>
                            <BrokenConditionsList>
                                {brokenTableConditions.map((condition, index) => (
                                    <BrokenConditionItem key={`broken-condition-${index}`}>
                                        {formatBrokenCondition(condition)}
                                    </BrokenConditionItem>
                                ))}
                            </BrokenConditionsList>
                        </>)}
                        {brokenRowConditions.length > 0 && (<>
                            <BrokenConditionsList>
                                {brokenRowConditions.map((condition, index) => (
                                    <BrokenConditionItem key={`broken-row-condition-${index}`}>
                                        {formatBrokenCondition(condition)}
                                    </BrokenConditionItem>
                                ))}
                            </BrokenConditionsList>
                        </>)}
                    </BrokenConditionsContainer>
                )}
            </TableHeader>
            <TableFilterContainer>
                {/* Filters will be placed here later */}
            </TableFilterContainer>
            <TableBody>
                {columns.length === 0 ? (
                    <EmptyState>{emptyStateMessage}</EmptyState>
                ) : (
                    <StyledTable>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <TableHeadCell
                                        key={column}
                                        onClick={() => handleColumnHeaderClick(column)}
                                        onKeyDown={(event) =>
                                            handleColumnHeaderKeyDown(event, column)
                                        }
                                        tabIndex={0}
                                        aria-sort={
                                            sortConfig?.column === column
                                                ? sortConfig.direction === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : 'none'
                                        }
                                        $isSortable
                                    >
                                        {column}
                                        {sortConfig?.column === column &&
                                            ` (${sortConfig.direction})`}
                                    </TableHeadCell>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row, rowIndex) => {
                                return (
                                    <TableRow key={row.id || rowIndex} $isBroken={row._meta?.brokenConditions}>
                                        {columns.map((column, columnIndex) => (
                                            <TableCell
                                                key={`${rowIndex}-${column}`}
                                                isFirstColumn={columnIndex === 0}
                                            >
                                                {formatCellValue(row[column])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                )
                            })}
                        </tbody>
                    </StyledTable>
                )}
            </TableBody>
            <CustomSqlTableModal
                show={showModal}
                tableId={tableId}
                initialValues={{
                    name: displayName,
                    description: displayDescription,
                    cgptPrompt: displayChatGptPrompt,
                    sqlQuery: displayQuery,
                    columnOrder: displayColumnOrder,
                    tableNumber: displayTableNumber,
                    tableGroup: displayTableGroup,
                    notifyIf: displayNotifyIf,
                }}
                databaseStructure={databaseStructure}
                onClose={handleCloseModal}
                onSave={handleModalSave}
                onDelete={handleDelete}
                onTest={testCustomSqlQuery}
                formatCellValue={formatCellValue}
            />
        </TableContainer>
    );
};

export default CustomSqlTable;

